
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {date, QBadge, QSelect} from "quasar"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {
  PageResource,
  PageSection,
  PCReport,
  PDF_SOURCE_PAGE,
  PdfResource,
  ReportPage,
  SectionItem,
} from "@/report/PCReport"
import {components} from "@/report/pages/Components"
import {Company, Plan, Platform} from "@/superannuation/PlanService"
import {format, startOfMonth, subDays, subYears} from "date-fns"
import {getIsoDate} from "@/utils/formatter"
import _ from "lodash"
import SelectDropDown from "@/components/SelectDropDown.vue"
import PopupDate from "@/components/PopupDate.vue"
import Message from "@/components/Message.vue"
import ExpandButton from "@/components/ExpandButton.vue"
import CloseButton from "@/components/CloseButton.vue"
import AddDeleteButton from "@/components/AddDeleteButton.vue"
import {getXsrfHeader, restDownloadFile} from "@/utils/rest"

interface PCReportRequest {
  id: string | null
  platformId: string | null
  companyId: string | null
  templateName: string | null
  reportingStartDate: string | null
  reportingEndDate: string | null
  reportPages: Array<ReportPage> | null
}

@Component({
  components: {
    CloseButton,
    AddDeleteButton,
    ExpandButton,
    PopupDate,
    Message,
    PageHeader,
    HelpIcon,
    QSelect,
    QBadge,
    SelectDropDown,
  },
})
export default class PcrTemplateView extends Vue {
  getXsrfHeader = getXsrfHeader

  readonly reportColumns = [
    {name: "id", label: "Template Id", field: "id"},
    {
      name: "platform",
      label: "Platform",
      field: "platformName",
      sortable: true,
    },
    {name: "template", label: "Template", field: "templateName"},
    {
      name: "start",
      label: "Start Date",
      field: "reportingStartDate",
      sortable: true,
      format: (dateString: string) => date.formatDate(dateString, "Do MMM YY"),
    },
    {
      name: "end",
      label: "As At",
      field: "reportingEndDate",
      sortable: true,
      format: (dateString: string) => date.formatDate(dateString, "Do MMM YY"),
    },
    {name: "company", label: "Company", field: "companyName", sortable: true},
  ]

  readonly pageColumns = [
    {name: "id", label: "Page Id", field: "id"},
    {
      name: "number",
      label: "Number",
      field: "templatePageNumber",
      sortable: true,
    },
    {name: "sequence", label: "Sequence", field: "sequence", sortable: true},
    {name: "name", label: "Name", field: "templatePageName", sortable: true},
    {name: "heading", label: "Heading", field: "heading", sortable: true},
  ]

  readonly sectionColumns = [
    {name: "id", label: "Section Id", field: "id"},
    {name: "sequence", label: "Sequence", field: "sequence", sortable: true},
    {name: "name", label: "Name", field: "templateSectionName", sortable: true},
    {name: "heading", label: "Heading", field: "heading", sortable: true},
  ]

  readonly itemColumns = [
    {name: "id", label: "Item Id", field: "id"},
    {name: "sequence", label: "Sequence", field: "sequence", sortable: true},
    {name: "note", label: "Note", field: "note"},
    {name: "text", label: "Text", field: "text"},
  ]

  readonly pdfColumns = [
    {
      name: "platform",
      label: "Platform",
      field: "platformName",
      sortable: true,
    },
    {name: "filename", label: "Filename", field: "filename"},
    {
      name: "modified",
      label: "Modified",
      field: "modified",
      sortable: true,
      format: (dateString: string) => date.formatDate(dateString, "Do MMM YY"),
    },
    {name: "pages", label: "Pages", field: "pages"},
    {
      name: "size",
      label: "Size",
      field: "size",
      sortable: true,
      format: (sizeString: string) =>
        `${Intl.NumberFormat().format(Math.round(Number(sizeString) / 1024))} KB`,
    },
  ]

  $refs: {
    platformSelect: QSelect
  }

  platformId: string | null = null
  planId: string | null = null
  companyId: string | null = null

  platforms: Array<Platform> | null = null
  plans: Array<Plan> | null = null
  companies: Array<Company> | null = null
  filteredPlans: Array<Plan> | null = null
  resources: Array<PageResource> | null = null
  pdfResources: Array<PdfResource> | null = null

  templates: Array<PCReport> = new Array<PCReport>()
  maintainReport: PCReportRequest | null = null
  maintainPage: ReportPage | null = null
  maintainSection: PageSection | null = null
  maintainItem: SectionItem | null = null
  uploadPlatformId: string | null = null
  uploadPageNumbers: string | null = null
  uploadFiles: Array<File> = new Array<File>()

  message: string | null = null
  error = false

  xhr: XMLHttpRequest | null = null
  rejection: string | null = null

  dataInProgress = false

  showReport = false
  reportUpdate = false
  reportOverride = false
  showPage = false
  pageUpdate = false
  showSection = false
  sectionUpdate = false
  showItem = false
  itemUpdate = false
  showUpload = false

  async created() {
    await this.getTemplates()
    await this.getPlatforms()
    await this.getPlans()
    await this.getCompanies()
    await this.getPageResources()
    await this.getPdfResources()
  }

  get isReportDisabled(): boolean {
    return (
      this.maintainReport == null ||
      this.maintainReport.platformId === null ||
      _.isEmpty(this.maintainReport.templateName) ||
      _.isEmpty(this.maintainReport.reportingStartDate) ||
      _.isEmpty(this.maintainReport.reportingEndDate)
    )
  }

  private isSourceInvalid(page: ReportPage): boolean {
    return (
      this.pdfSourcePageTemplate &&
      (_.isEmpty(page.sourceFilename) ||
        page.sourcePageNumber === null ||
        page.sourcePageNumber <= 0)
    )
  }

  get isPageDisabled(): boolean {
    return (
      this.maintainPage == null ||
      this.maintainPage.templatePageNumber <= 0 ||
      _.isEmpty(this.maintainPage.templatePageName) ||
      this.maintainPage.sequence <= 0 ||
      this.isSourceInvalid(this.maintainPage)
    )
  }

  get isSectionDisabled(): boolean {
    return (
      this.maintainSection == null ||
      _.isEmpty(this.maintainSection.templateSectionName) ||
      this.maintainSection.sequence <= 0
    )
  }

  get isItemDisabled(): boolean {
    return (
      this.maintainItem == null ||
      this.maintainItem.sequence <= 0 ||
      _.isEmpty(this.maintainItem.text)
    )
  }

  private async getTemplates() {
    this.dataInProgress = true
    await Axios.get(`/api/pcr`, {
      params: {
        platformId: this.platformId,
        planId: this.planId,
        companyId: this.companyId,
      },
    })
      .then((response: AxiosResponse) => {
        this.message = null
        this.templates = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private async getPlatforms() {
    await Axios.get(`/api/platforms?name=`).then((response: AxiosResponse) => {
      this.platforms = response.data
    })
  }

  private async getPlans() {
    await Axios.get(`/api/plan?name=`).then((response: AxiosResponse) => {
      this.plans = response.data
      this.filteredPlans = this.plans
    })
  }

  private async getCompanies() {
    await Axios.get(`/api/companies?name=`).then((response: AxiosResponse) => {
      this.companies = response.data
    })
  }

  private async getPageResources() {
    await Axios.get(`/api/pcr/resources`).then((response: AxiosResponse) => {
      this.resources = response.data
    })
  }

  private async getPdfResources() {
    await Axios.get(`/api/pcr/upload`, {
      params: {platformId: this.platformId},
    }).then((response: AxiosResponse) => {
      this.pdfResources = response.data
    })
  }

  private formatDisplay(displayDate: Date): string {
    return format(displayDate, "dd/MM/yyyy")
  }

  private defaultMaintainReport(report: PCReport) {
    this.maintainReport = {
      id: report.id,
      platformId: report.platformId,
      companyId: report.companyId,
      templateName: report.templateName,
      reportingStartDate: this.formatDisplay(new Date(report.reportingStartDate)),
      reportingEndDate: this.formatDisplay(new Date(report.reportingEndDate)),
      reportPages: report.reportPages,
    }
  }

  private showUpdateReport(report: PCReport) {
    this.defaultMaintainReport(report)
    this.reportUpdate = true
    this.showReport = true
  }

  private newReport(): PCReportRequest {
    return {
      id: null,
      platformId: this.platformId,
      companyId: this.companyId,
      templateName: "DEFAULT",
      reportingStartDate: this.formatDisplay(subYears(startOfMonth(Date.now()), 1)),
      reportingEndDate: this.formatDisplay(subDays(startOfMonth(Date.now()), 1)),
      reportPages: null,
    }
  }

  private overridenReport(report: PCReport): PCReportRequest {
    return {
      id: null,
      platformId: report.platformId,
      companyId: null,
      templateName: report.templateName,
      reportingStartDate: this.formatDisplay(new Date(report.reportingStartDate)),
      reportingEndDate: this.formatDisplay(new Date(report.reportingEndDate)),
      reportPages: null,
    }
  }

  private showCreateReport(report: PCReport | null = null) {
    this.maintainReport = report == null ? this.newReport() : this.overridenReport(report)
    this.reportUpdate = false
    this.showReport = true
  }

  private showUpdatePage(page: ReportPage, report: PCReport) {
    this.defaultMaintainReport(report)
    this.maintainPage = page
    this.pageUpdate = true
    this.showPage = true
  }

  private showCreatePage(report: PCReport) {
    this.defaultMaintainReport(report)
    const lastPage = _.last(report.reportPages)
    const nextPageNumber = (lastPage ? lastPage!.templatePageNumber : 0) + 1
    const nextSequence = (lastPage ? lastPage!.sequence : 0) + 1
    this.maintainPage = {
      id: null,
      templatePageNumber: nextPageNumber,
      templatePageName: null,
      sequence: nextSequence,
      heading: null,
      sourceFilename: null,
      sourcePageNumber: null,
      pageSections: new Array<PageSection>(),
      pageResources: new Array<PageResource>(),
    }
    this.pageUpdate = false
    this.showPage = true
  }

  private showUpdateSection(section: PageSection, page: ReportPage, report: PCReport) {
    this.defaultMaintainReport(report)
    this.maintainPage = page
    this.maintainSection = section
    this.sectionUpdate = true
    this.showSection = true
  }

  private showCreateSection(page: ReportPage, report: PCReport) {
    this.defaultMaintainReport(report)
    this.maintainPage = page
    const lastSection = _.last(page.pageSections)
    const nextSequence = (lastSection ? lastSection!.sequence : 0) + 1
    this.maintainSection = {
      id: null,
      templateSectionName: null,
      sequence: nextSequence,
      heading: null,
      sectionItems: new Array<SectionItem>(),
    }
    this.sectionUpdate = false
    this.showSection = true
  }

  private showUpdateItem(
    item: SectionItem,
    section: PageSection,
    page: ReportPage,
    report: PCReport
  ) {
    this.defaultMaintainReport(report)
    this.maintainPage = page
    this.maintainSection = section
    this.maintainItem = item
    this.itemUpdate = true
    this.showItem = true
  }

  private showCreateItem(section: PageSection, page: ReportPage, report: PCReport) {
    this.defaultMaintainReport(report)
    this.maintainPage = page
    this.maintainSection = section

    const lastItem = _.last(section.sectionItems)
    const nextSequence = (lastItem ? lastItem!.sequence : 0) + 1
    this.maintainItem = {
      id: null,
      sequence: nextSequence,
      text: null,
      note: null,
    }
    this.itemUpdate = false
    this.showItem = true
  }

  private reportThen = () => {
    this.error = false
    this.message = null
    void this.getTemplates()
  }

  private async persistReport() {
    this.showReport = false

    if (!this.isReportDisabled) {
      this.dataInProgress = true
      this.maintainReport!.reportingStartDate = getIsoDate(this.maintainReport!.reportingStartDate)
      this.maintainReport!.reportingEndDate = getIsoDate(this.maintainReport!.reportingEndDate)

      void (await Axios.post(`/api/pcr`, this.maintainReport)
        .then(this.reportThen)
        .catch((reason: AxiosError<any>) => {
          this.error = true
          this.message = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false)))
    }
  }

  private async deleteReportPath(report: PCReport, path = "") {
    const fullPath =
      report.planId == null
        ? `/api/platforms/${report.platformId}/reports/${report.id}${path}`
        : `/api/plans/${report.planId}/reports/${report.id}${path}`
    void Axios.delete(fullPath)
      .then(this.reportThen)
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private deleteReport(report: PCReport) {
    void this.deleteReportPath(report)
  }

  private deletePage(page: ReportPage, report: PCReport) {
    void this.deleteReportPath(report, `/pages/${page.id!}`)
  }

  private deleteSection(section: PageSection, page: ReportPage, report: PCReport) {
    void this.deleteReportPath(report, `/pages/${page.id!}/sections/${section.id!}`)
  }

  private deleteItem(item: SectionItem, section: PageSection, page: ReportPage, report: PCReport) {
    void this.deleteReportPath(
      report,
      `/pages/${page.id!}/sections/${section.id!}/items/${item.id!}`
    )
  }

  private async persistPage() {
    this.showPage = false
    if (!this.isPageDisabled) {
      const reportPages = this.maintainReport!.reportPages!
      if (this.maintainPage!.id === null) {
        reportPages.push(this.maintainPage!)
      } else {
        const i = reportPages.findIndex(rp => rp.id === this.maintainPage!.id)
        reportPages[i] = this.maintainPage!
      }
      await this.persistReport()
    }
  }

  private async persistSection() {
    this.showSection = false
    if (!this.isSectionDisabled) {
      const pageSections = this.maintainPage!.pageSections
      if (this.maintainSection!.id === null) {
        pageSections.push(this.maintainSection!)
      } else {
        const i = pageSections.findIndex(ps => ps.id === this.maintainSection!.id)
        pageSections[i] = this.maintainSection!
      }
      await this.persistReport()
    }
  }

  private async persistItem() {
    this.showItem = false
    if (!this.isItemDisabled) {
      const sectionItems = this.maintainSection!.sectionItems
      if (this.maintainItem!.id === null) {
        sectionItems.push(this.maintainItem!)
      } else {
        const i = sectionItems.findIndex(si => si.id === this.maintainItem!.id)
        sectionItems[i] = this.maintainItem!
      }
      await this.persistReport()
    }
  }

  private platformSelected(platform: Platform | null) {
    this.platformId = platform == null ? null : platform.id
    if (platform != null) {
      this.planId = null
    }
    void this.getTemplates()
    void this.getPdfResources()
  }

  private planSelected(plan: Plan | null) {
    this.planId = plan == null ? null : plan.id
    if (plan != null) {
      this.companyId = null
    }
    void this.getTemplates()
  }

  private companySelected(company: Company | null) {
    this.companyId = company == null ? null : company.id
    void this.getTemplates()
  }

  get components(): Array<string> {
    return [...components(), PDF_SOURCE_PAGE]
  }

  get filteredCompanies() {
    return this.companies!.filter(
      c =>
        this.maintainReport!.platformId == null ||
        c.plan.platform.id === this.maintainReport!.platformId
    )
  }

  private showUploadPdf() {
    this.uploadPlatformId = this.platformId
    this.showUpload = true
  }

  get uploadUrl(): string | null {
    return "/api/pcr/upload".concat(
      (this.uploadPlatformId == null ? "" : `?platformId=${this.uploadPlatformId}`).concat(
        _.isEmpty(this.uploadPageNumbers)
          ? ""
          : (this.uploadPlatformId == null ? "?" : "&").concat(
              `pageNumbers=${this.uploadPageNumbers!}`
            )
      )
    )
  }

  private failed(info: {files: Array<File>; xhr: XMLHttpRequest}) {
    this.xhr = info.xhr
    this.error = true
    this.message = JSON.parse(this.xhr.response).message
  }

  private rejected(rejectedEntries: Array<{failedPropValidation: string; file: File}>) {
    const entry = rejectedEntries.find(Boolean)
    this.error = true
    this.message = `File: '${entry!.file.name}' type: '${entry!.file.type}' did not pass: '${
      entry!.failedPropValidation
    }' validation constraint`
    this.uploadFiles = this.uploadFiles.filter(uf => uf !== entry!.file)
  }

  private added(files: Array<File>) {
    this.error = false
    this.message = null
    this.uploadFiles = files
  }

  private removed(files: Array<File>) {
    this.uploadFiles = this.uploadFiles.filter(uf => files.includes(uf))
  }

  private uploaded() {
    this.xhr = null
    void this.getPdfResources()
    this.uploadFiles = new Array<File>()
    this.uploadPageNumbers = null
  }

  private async downloadPdf(pdf: PdfResource) {
    await restDownloadFile(
      `/api/pcr/download/${pdf.filename}`,
      pdf.filename,
      pdf.platformId == null ? undefined : {platformId: pdf.platformId}
    )
  }

  private deleteThen = () => {
    void this.getPdfResources()
  }

  private async deletePdf(pdf: PdfResource) {
    await Axios.delete("/api/pcr/upload", {
      params: {filename: pdf.filename, platformId: pdf.platformId},
    })
      .then(this.deleteThen)
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  get pdfSourcePageTemplate(): boolean {
    return this.maintainPage!.templatePageName === PDF_SOURCE_PAGE
  }

  private isPdfSourcePageTemplate(page: ReportPage): boolean {
    return page.templatePageName === PDF_SOURCE_PAGE
  }

  get filteredPdfFiles(): Array<string> {
    return this.pdfResources!.filter(
      pr => pr.platformId === null || pr.platformId === this.maintainReport!.platformId
    ).map(pr => pr.filename)
  }
}
